import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '../../../../../shared/_TBComponents/Modal';
import Input from '../../../../../shared/_TBComponents/Input';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import ModalHeading from '../ModalHeading';
import TagModalShell, { Field, FieldLabel } from './TagModalShell';
import { showErrorToast, showSuccessToast } from '../../../../../shared/utils/toastManager';
import { EXERCISE_TYPES_ALLOWING_TAGS } from '../../../../../shared/constants';
import {
  createTagForAccount,
  updateTagForAccount,
  setTagExerciseAssignments,
} from '../../../../calendar/ducks/calendarActions';

// Tag PUT is rename-only, so the "Associated Exercises" picker saves by
// PATCHing each exercise whose membership changed (see setTagExerciseAssignments).

const ExerciseTagModal = ({
  isOpen, onClose, accountCode, tag, onSaved,
}) => {
  const dispatch = useDispatch();
  const allExercises = useSelector((state) => state.calendar.data.allExercises);

  const [name, setName] = useState('');
  const [exerciseIds, setExerciseIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(tag?.name || '');
    setExerciseIds((tag?.exercises || []).map((e) => e.id));
  }, [isOpen, tag]);

  // Account exercise pool limited to tag-compatible types (the API rejects
  // tags on anything else), plus any already-tagged exercise missing from it
  // (e.g. read-only linked rows) so an existing association isn't dropped.
  const exerciseOptions = useMemo(() => {
    const byId = new Map();
    (allExercises || []).forEach((e) => {
      if (e.name && !e.isReadOnly && EXERCISE_TYPES_ALLOWING_TAGS.includes(e.type)) {
        byId.set(e.id, { id: e.id, name: e.name });
      }
    });
    (tag?.exercises || []).forEach((e) => {
      if (!byId.has(e.id)) byId.set(e.id, { id: e.id, name: e.name });
    });
    return [...byId.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allExercises, tag]);

  const selectedExerciseOptions = exerciseIds
    .map((id) => exerciseOptions.find((o) => o.id === id))
    .filter(Boolean);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      showErrorToast('Tag name is required');
      return;
    }
    const originalIds = (tag?.exercises || []).map((e) => e.id);
    const addIds = exerciseIds.filter((id) => !originalIds.includes(id));
    const removeIds = originalIds.filter((id) => !exerciseIds.includes(id));

    const finish = (toast) => {
      if (toast) showSuccessToast(toast);
      setIsSaving(false);
      onSaved();
      onClose();
    };
    const fail = () => setIsSaving(false);

    setIsSaving(true);
    if (!tag) {
      dispatch(createTagForAccount(accountCode, trimmed, {
        onSuccess: (newTag) => {
          if (!addIds.length) {
            finish('Tag created');
            return;
          }
          dispatch(setTagExerciseAssignments(accountCode, newTag.id, { addIds }, {
            onSuccess: () => finish('Tag created'),
            // The tag itself exists; close + refetch so the table shows reality.
            onError: () => finish(),
          }));
        },
        onError: (message) => {
          showErrorToast(message || 'Unable to create tag');
          fail();
        },
      }));
      return;
    }

    const renamed = trimmed !== tag.name;
    const assign = () => {
      if (!addIds.length && !removeIds.length) {
        finish();
        return;
      }
      dispatch(setTagExerciseAssignments(accountCode, tag.id, { addIds, removeIds }, {
        // updateTagForAccount already toasts on rename; only toast here when
        // the assignments were the whole change.
        onSuccess: () => finish(renamed ? null : 'Tag updated'),
        onError: () => finish(),
      }));
    };
    if (!renamed && !addIds.length && !removeIds.length) {
      setIsSaving(false);
      onClose();
      return;
    }
    if (renamed) {
      dispatch(updateTagForAccount(accountCode, tag.id, trimmed, {
        onSuccess: assign,
        onError: fail,
      }));
    } else {
      assign();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      customMaxWidth='480px'
      customMinHeight='0'
      customPadding='32px'
      customBorderRadius='8px'
      hideButton
    >
      {isOpen && (
        <TagModalShell
          onCancel={onClose}
          onSave={handleSave}
          isSaving={isSaving}
          saveDisabled={isSaving}
        >
          <ModalHeading title={tag ? 'Edit Tag' : 'New Tag'} onClose={onClose} />
          <Field>
            <FieldLabel>Tag name</FieldLabel>
            <Input
              name='exercise-tag-name'
              inputOnly
              value={name}
              placeholder='Tag name'
              onChange={(e) => setName(e.target.value)}
              styledPropsInput={{ width: '100%', height: '40px', fontSize: '14px' }}
            />
          </Field>
          <Field>
            <SelectBox
              title='Associated Exercises (optional)'
              titleStyledProps={{ fontSize: '14px', fontWeight: 700 }}
              options={exerciseOptions}
              value={selectedExerciseOptions}
              isMulti
              onChangeFunction={(selected) => setExerciseIds((selected || []).map((o) => o.id))}
            />
          </Field>
        </TagModalShell>
      )}
    </Modal>
  );
};

ExerciseTagModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  tag: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    exercises: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
  }),
  onSaved: PropTypes.func,
};

ExerciseTagModal.defaultProps = {
  accountCode: undefined,
  tag: null,
  onSaved: () => {},
};

export default ExerciseTagModal;
